import { createLoadingScreen } from "../DomUtilities/ElementsCreator";
import { isValidCVR } from "../Validations/Validations";
import Store from "../Models/Store";
import { DeliveryMethod } from "../Models/DeliveryMethod";
import EConomicData from "../Models/EConomicData";
import CustomerHeadquarter from "../Models/CustomerHeadquarter";

export default class StoreBaseModal {
	loadingScreen: HTMLElement;
	form: HTMLFormElement;
	priceDifferenceSignSelect: HTMLSelectElement;
	priceDifferenceInput: HTMLInputElement;
	isPriceInputEnabled: boolean;

	constructor() {
		this.loadingScreen = createLoadingScreen();
		this.isPriceInputEnabled = false;
	}

	protected get modalData(): { valid: boolean, store: Store } {
		const inputs = this.form.elements;
		let store: Store = new Store();
		let valid: boolean = this.form.checkValidity() && isValidCVR(store.cvr) && this.isCustomerContactValid;
		const props: Array<string> = Object.keys(store);

		for (let i = 0; i < props.length; i++) {
			let input = (inputs[i] as HTMLInputElement);
			if (input) {
				store[props[i]] = input.value;
			}
		}

		if (!store.productionFacilityId) {
			delete store.productionFacilityId;
		}

		store.priceDifferencePercent = this.priceDifference;

		const deliveryCheckbox: HTMLInputElement = document.querySelector('#delivery');
		store.deliveryMethod = deliveryCheckbox.checked ? DeliveryMethod.Mail : DeliveryMethod.Chauffeur;

		const straussCheckbox: HTMLInputElement = document.querySelector('#domain');
		store.isDomainStore = straussCheckbox.checked;

		store.eConomicData = this.eConomicData;

		return { valid, store };
	}

	protected addCVRValidator() {
		const cvrInput: HTMLInputElement = this.form.querySelector("input[name='cvr']");
		const errorText: HTMLParagraphElement = this.form.querySelector("p.cvr");
		const labelAfterCVR: HTMLLabelElement = this.form.querySelector("p.cvr + label");
		cvrInput.oninput = () => {
			if (cvrInput.validity.valid) {
				if (isValidCVR(cvrInput.value)) {
					errorText.style.display = "none";
					labelAfterCVR.style.marginTop = "20px";
					cvrInput.style.borderColor = "#bdbdbd";
				} else {
					errorText.style.display = "block";
					labelAfterCVR.style.marginTop = "5px";
					cvrInput.style.borderColor = "red";
				}
			} else {
				errorText.style.display = null;
				labelAfterCVR.style.marginTop = null;
				cvrInput.style.borderColor = null;
			}
		}
	}

	protected changeValue(input: HTMLInputElement, addSign: boolean) {
		if (this.isPriceInputEnabled) {
			let value: number = parseFloat(input.value);
			if (addSign) {
				value += 0.5;
			} else {
				if (value - 0.5 >= 0) {
					value -= 0.5;
				}
			}

			input.value = value.toFixed(1).toString();
		}
	}

	private get isCustomerContactValid(): boolean {
		const eConomicCustomerContactForm: HTMLFormElement = document.querySelector("#economic-customer-contact-form");
		const eConomicCustomerContactcheckbox: HTMLInputElement = document.querySelector("#economic-customer-contact");

		return eConomicCustomerContactcheckbox === null
			|| !eConomicCustomerContactcheckbox.checked
			|| (eConomicCustomerContactcheckbox.checked
				&& eConomicCustomerContactForm.checkValidity());
	}

	private get priceDifference(): number {
		this.priceDifferenceSignSelect = this.form.querySelector('.price-difference select');
		const priceDifferenceSign = +this.priceDifferenceSignSelect.value;
		this.priceDifferenceInput = this.form.querySelector('.price-difference input');
		const priceDifferenceValue = +this.priceDifferenceInput.value || 0;

		return priceDifferenceSign * priceDifferenceValue;
	}

	private get eConomicData(): EConomicData {
		const economicCheckBox: HTMLInputElement = document.querySelector("#economic");
		const customerContactCheckBox: HTMLInputElement = document.querySelector("#economic-customer-contact");

		if (economicCheckBox && (economicCheckBox.checked || economicCheckBox.disabled)) {
			const economicData = new EConomicData();
			const economicForm: HTMLFormElement = document.querySelector("form#economic-form");
			const formElements = economicForm.elements;

			for (let i = 0; i < formElements.length; i++) {
				let select: HTMLInputElement = formElements[i] as HTMLInputElement;
				economicData[select.name] = select.value;
			}

			if (customerContactCheckBox.checked) {
				const wrapper: HTMLDivElement = document.querySelector("#economic-customer-contact-wrapper");
				const eConomicCustomerContactForm: HTMLFormElement = document.querySelector("#economic-customer-contact-form");
				const customerContactFormElements = eConomicCustomerContactForm.elements;

				economicData.customerHeadquarter = new CustomerHeadquarter();
				if (wrapper.dataset.customercontactnumber) {
					economicData.customerHeadquarter.attentionNumber = parseInt(wrapper.dataset.customercontactnumber);
				}

				for (let i = 0; i < customerContactFormElements.length; i++) {
					let input: HTMLInputElement = customerContactFormElements[i] as HTMLInputElement;
					economicData.customerHeadquarter[input.name] = input.value;
				}
			}

			return economicData;
		}

		return undefined;
	}

	private validateNumber(input: HTMLInputElement) {
		const regex = /^\d{1,2}([.][0-9]\d?)?$|^100$/;

		if (input.value && !regex.test(input.value)) {
			input.value = input.value.slice(0, -1);
		}

		if (!input.value) {
			input.value = "0";
		}
	}

	protected selectFormFields() {
		this.form = document.querySelector("#store-modal");
		this.priceDifferenceSignSelect = this.form.querySelector('.price-difference select');
		this.priceDifferenceInput = this.form.querySelector('.price-difference input');
		this.priceDifferenceInput.disabled = true;
		this.addPriceDifferenceListener();
	}

	protected addPriceDifferenceListener() {
		this.priceDifferenceSignSelect.onchange = () => {
			this.priceDifferenceInput.disabled = false;
			this.isPriceInputEnabled = true;
		}

		this.priceDifferenceInput.oninput = () => {
			this.validateNumber(this.priceDifferenceInput);
		}

		const imgIncreaseLonger: HTMLImageElement =
			this.form.querySelector('input[name="priceDifference"] + div.input-number img[alt="increase icon"]');
		imgIncreaseLonger.onclick = () => {
			this.changeValue(this.priceDifferenceInput, true);
		}

		const imgDecreaseLonger: HTMLImageElement =
			this.form.querySelector('input[name="priceDifference"] + div.input-number img[alt="decrease icon"]');
		imgDecreaseLonger.onclick = () => {
			this.changeValue(this.priceDifferenceInput, false);
		}
	}
}
